import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import TraditionalPattern from '../components/TraditionalPattern';
import { getImageUrl } from '../utils/imageUtils';

const Profile = () => {
  const { user, isAuthenticated, loading, updateProfile, loadUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleEditToggle = () => {
    if (!isEditing) {
      // Passer en mode édition - pré-remplir le formulaire
      setFormData({
        nom: user?.nom || '',
        email: user?.email || '',
        telephone: user?.telephone || '',
        whatsapp: user?.whatsapp || '',
        specialite: user?.specialite || '',
        region: user?.region || '',
        adresse: user?.adresse || '',
        description: user?.description || '',
      });
      setPhotoPreview(null);
      setPhotoFile(null);
    }
    setIsEditing(!isEditing);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Valider le type et la taille
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille de l\'image ne doit pas dépasser 5 Mo');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();

      // Ajouter les champs texte
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Ajouter la photo si sélectionnée
      if (photoFile) {
        formDataToSend.append('photoProfil', photoFile);
      }

      const result = await updateProfile(formDataToSend);
      
      if (result.success) {
        setSuccess(result.message);
        setIsEditing(false);
        // Recharger les données utilisateur
        await loadUser();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const getProfileImageUrl = () => {
    if (photoPreview) return photoPreview;
    if (user?.photoProfil) {
      if (user.photoProfil.startsWith('http')) return user.photoProfil;
      return getImageUrl(user.photoProfil);
    }
    return null;
  };

  const regions = [
    'Dakar', 'Thiès', 'Saint-Louis', 'Kaolack', 'Ziguinchor',
    'Tambacounda', 'Kolda', 'Fatick', 'Kaffrine', 'Kédougou',
    'Louga', 'Matam', 'Sédhiou', 'Diourbel'
  ];

  const specialites = [
    'Textile et Tissage',
    'Poterie et Céramique',
    'Sculpture sur Bois',
    'Maroquinerie',
    'Bijouterie Traditionnelle',
    'Vannerie',
    'Ferronnerie',
    'Peinture et Art',
    'Instruments de Musique',
    'Couture',
    'Décoration',
    'Autre'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary-800 mb-4"></i>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header avec photo de profil */}
        <div className="bg-gradient-to-r from-primary-800 to-primary-900 rounded-2xl shadow-xl overflow-hidden mb-8 relative">
          <TraditionalPattern variant="waves" opacity={0.15} className="text-accent-400" />
          <div className="px-8 py-12 relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent-400/20 rounded-full blur-3xl"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
              {/* Photo de profil */}
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/20 shadow-xl bg-white/10">
                  {getProfileImageUrl() ? (
                    <img
                      src={getProfileImageUrl()}
                      alt={user?.nom}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent-400 to-accent-600">
                      <i className="fas fa-user text-5xl text-white"></i>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Changer la photo"
                  >
                    <i className="fas fa-camera text-white text-2xl"></i>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>

              {/* Infos header */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-2">
                  {user?.nom}
                </h1>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-yellow-100">
                  {user?.specialite && (
                    <span className="flex items-center gap-2">
                      <i className="fas fa-hammer"></i>
                      {user.specialite}
                    </span>
                  )}
                  {user?.region && (
                    <span className="flex items-center gap-2">
                      <i className="fas fa-map-marker-alt"></i>
                      {user.region}
                    </span>
                  )}
                  {user?.estVerifie && (
                    <span className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full text-green-200">
                      <i className="fas fa-check-circle"></i>
                      Vérifié
                    </span>
                  )}
                </div>
              </div>

              {/* Actions header */}
              <div className="flex gap-3">
                {!isEditing ? (
                  <>
                    <Button
                      onClick={handleEditToggle}
                      variant="secondary"
                      size="md"
                      className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    >
                      <i className="fas fa-edit mr-2"></i>
                      Modifier
                    </Button>
                    <Link to="/mes-produits">
                      <Button variant="primary" size="md">
                        <i className="fas fa-box mr-2"></i>
                        Mes Produits
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Button
                    onClick={handleEditToggle}
                    variant="ghost"
                    size="md"
                    className="bg-white/20 hover:bg-white/30 text-white"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Annuler
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-800">
            <i className="fas fa-check-circle"></i>
            <span>{success}</span>
          </div>
        )}

        {/* Formulaire / Affichage */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {!isEditing ? (
            // Mode lecture
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Informations personnelles */}
              <div>
                <h2 className="text-xl font-display font-bold text-primary-900 mb-6 flex items-center gap-2">
                  <i className="fas fa-user text-accent-500"></i>
                  Informations personnelles
                </h2>
                <div className="space-y-4">
                  <InfoField label="Email" value={user?.email} icon="fas fa-envelope" />
                  <InfoField label="Téléphone" value={user?.telephone} icon="fas fa-phone" />
                  <InfoField label="WhatsApp" value={user?.whatsapp} icon="fab fa-whatsapp" />
                </div>
              </div>

              {/* Activité artisanale */}
              <div>
                <h2 className="text-xl font-display font-bold text-primary-900 mb-6 flex items-center gap-2">
                  <i className="fas fa-briefcase text-accent-500"></i>
                  Activité artisanale
                </h2>
                <div className="space-y-4">
                  <InfoField label="Spécialité" value={user?.specialite} icon="fas fa-hammer" />
                  <InfoField label="Région" value={user?.region} icon="fas fa-map-marker-alt" />
                  <InfoField label="Adresse" value={user?.adresse} icon="fas fa-location-dot" />
                </div>
              </div>

              {/* Description complète */}
              {user?.description && (
                <div className="md:col-span-2">
                  <h2 className="text-xl font-display font-bold text-primary-900 mb-6 flex items-center gap-2">
                    <i className="fas fa-align-left text-accent-500"></i>
                    À propos
                  </h2>
                  <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {user.description}
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Mode édition
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations personnelles */}
                <div className="md:col-span-2">
                  <h2 className="text-xl font-display font-bold text-primary-900 mb-6 flex items-center gap-2">
                    <i className="fas fa-user text-accent-500"></i>
                    Informations personnelles
                  </h2>
                </div>

                <InputField
                  label="Nom complet"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  icon="fas fa-user"
                  required
                />

                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  icon="fas fa-envelope"
                />

                <InputField
                  label="Téléphone"
                  name="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  icon="fas fa-phone"
                />

                <InputField
                  label="WhatsApp"
                  name="whatsapp"
                  type="tel"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  icon="fab fa-whatsapp"
                  placeholder="+221 XX XXX XX XX"
                />

                {/* Activité artisanale */}
                <div className="md:col-span-2 mt-4">
                  <h2 className="text-xl font-display font-bold text-primary-900 mb-6 flex items-center gap-2">
                    <i className="fas fa-briefcase text-accent-500"></i>
                    Activité artisanale
                  </h2>
                </div>

                <SelectField
                  label="Spécialité"
                  name="specialite"
                  value={formData.specialite}
                  onChange={handleInputChange}
                  options={specialites}
                  icon="fas fa-hammer"
                />

                <SelectField
                  label="Région"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  options={regions}
                  icon="fas fa-map-marker-alt"
                />

                <InputField
                  label="Adresse"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  icon="fas fa-location-dot"
                  className="md:col-span-2"
                />

                <TextAreaField
                  label="Description / Présentation"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Parlez-nous de votre savoir-faire, votre parcours, vos créations..."
                  rows={4}
                  className="md:col-span-2"
                />
              </div>

              {/* Actions */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex gap-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={saving}
                  className="flex-1 md:flex-initial"
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleEditToggle}
                  variant="ghost"
                  size="lg"
                  className="border border-gray-300"
                >
                  Annuler
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Composants helpers
const InfoField = ({ label, value, icon }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 bg-gradient-to-br from-accent-100 to-accent-200 rounded-lg flex items-center justify-center flex-shrink-0">
      <i className={`${icon} text-accent-700`}></i>
    </div>
    <div className="flex-1">
      <label className="block text-sm font-semibold text-gray-500 mb-1">{label}</label>
      <p className="text-gray-900 font-medium">{value || 'Non renseigné'}</p>
    </div>
  </div>
);

const InputField = ({ label, name, type = 'text', value, onChange, icon, required, placeholder, className }) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <i className={icon}></i>
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500 transition-colors"
      />
    </div>
  </div>
);

const SelectField = ({ label, name, value, onChange, options, icon }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
        <i className={icon}></i>
      </div>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500 transition-colors appearance-none bg-white"
      >
        <option value="">Sélectionner...</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        <i className="fas fa-chevron-down"></i>
      </div>
    </div>
  </div>
);

const TextAreaField = ({ label, name, value, onChange, placeholder, rows = 3, className }) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-300 focus:border-accent-500 transition-colors resize-none"
    />
  </div>
);

export default Profile;